/**
 * Secretary mode: the panel keeps its own address; who is looking decides. A logged-in secretary asks the gateway
 * «whose secretary am I?» and, once a doctor is chosen, every upstream call goes through the secretary gateway
 * (this app's own API routes, same origin) with `x-secretary-doctor-id`. The gateway checks the
 * secretary's own token against its access table, keeps the doctor's session on the server and forwards only
 * allow-listed paths — the secretary's browser never holds a doctor token.
 */
import { useSyncExternalStore } from 'react';
import type { SecretaryScope } from './scopes';

export const SECRETARY_GATEWAY_PREFIX = '/patient/api/secretary-gw';
export const SECRETARY_DOCTOR_HEADER = 'x-secretary-doctor-id';

const STORAGE_KEY = 'p24:secretary-doctor';

/** upstream origin → gateway sub-path */
export const UPSTREAM_PREFIXES: Record<string, string> = {
  'https://apigw.paziresh24.com': '/gw',
  'https://api.paziresh24.com/V1': '/v1',
  'https://drprofile.paziresh24.com': '/drprofile',
  'https://hamdast.paziresh24.com': '/hamdast',
};

let actingDoctorId: string | null = null;
let actingScopes: readonly SecretaryScope[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(listener => listener());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Set (or clear) the doctor the secretary is acting for; kept per tab so a reload stays in the same context. */
export const setActingDoctor = (doctorId: string | null, scopes: readonly SecretaryScope[] = []) => {
  const changed = actingDoctorId !== doctorId || actingScopes.join() !== scopes.join();
  actingDoctorId = doctorId;
  actingScopes = doctorId ? scopes : [];
  if (changed) emit();
  if (typeof window === 'undefined') return;
  try {
    if (doctorId) window.sessionStorage.setItem(STORAGE_KEY, doctorId);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked: the in-memory value still works for this page */
  }
};

export const getActingDoctor = (): string | null => {
  if (actingDoctorId) return actingDoctorId;
  if (typeof window === 'undefined') return null;
  try {
    actingDoctorId = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
  return actingDoctorId;
};

export const isSecretaryMode = () => getActingDoctor() !== null;

export const getActingScopes = (): readonly SecretaryScope[] => actingScopes;

/** Re-renders when a secretary starts or stops acting for a doctor (the first render, and the server, see «not acting»). */
export const useActingDoctor = (): string | null => useSyncExternalStore(subscribe, getActingDoctor, () => null);
const NO_SCOPES: readonly SecretaryScope[] = [];
export const useActingScopes = (): readonly SecretaryScope[] => useSyncExternalStore(subscribe, getActingScopes, () => NO_SCOPES);

/** Map an absolute upstream URL (or base URL) to the gateway; anything unknown is returned untouched. */
export const toSecretaryGatewayUrl = (url: string): string => {
  for (const [origin, prefix] of Object.entries(UPSTREAM_PREFIXES)) {
    if (url === origin || url.startsWith(`${origin}/`) || url.startsWith(`${origin}?`)) {
      return `${SECRETARY_GATEWAY_PREFIX}${prefix}${url.slice(origin.length)}`;
    }
  }
  return url;
};

/** Use for URLs built outside the shared axios instances (fetch, plain axios). A no-op for doctors. */
export const panelUrl = (url: string): string => (isSecretaryMode() ? toSecretaryGatewayUrl(url) : url);

/** Headers a gateway call needs (empty for doctors). For fetch-based calls. */
export const panelHeaders = (): Record<string, string> => {
  const doctorId = getActingDoctor();
  return doctorId ? { [SECRETARY_DOCTOR_HEADER]: doctorId } : {};
};
